import {
  calculateStatusFromStatusCode,
  humanizeTime
} from '@/pages/Administration/Devices/Devices.service';
import type {
  LimaSourceAggregatedStatusModel,
  StatusGroup
} from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';

import type { AggregatedSource } from './LimaSourceAggregatedStatus.types';

const createAggregatedSource = (
  device_uid: string,
  source_type_id: number,
  name: string,
  last_seen?: string
): AggregatedSource => ({
  device_uid,
  source_type_id,
  name,
  last_seen: last_seen ?? null,
  last_seen_display: humanizeTime(last_seen || '', true),
  status: 'Unknown',
  record_count: 0,
  group_1xxx: 0,
  group_2xxx: 0,
  group_3xxx: 0,
  group_4xxx: 0,
  group_null: 0,
  highest_status_code: null
});

const statusGroupMap: Record<string, keyof AggregatedSource> = {
  '1': 'group_1xxx',
  '2': 'group_2xxx',
  '3': 'group_3xxx',
  '4': 'group_4xxx'
};

const getHighestStatus = (bucket: AggregatedSource): string => {
  if ((bucket.group_4xxx ?? 0) > 0) return 'error';
  if ((bucket.group_3xxx ?? 0) > 0) return 'warning';
  if ((bucket.group_2xxx ?? 0) > 0) return 'success';
  if ((bucket.group_1xxx ?? 0) > 0) return 'disabled';
  return 'unknown';
};

// Group source status codes by source type
export default function groupSourceStatusCodes(
  data: LimaSourceAggregatedStatusModel[]
): LimaSourceAggregatedStatusModel[] {
  const aggregatedByDevice: Record<
    string,
    Record<number, AggregatedSource>
  > = {};

  data.forEach(
    ({
      device_uid,
      source_type_id,
      name,
      last_seen,
      status_code,
      record_count
    }) => {
      if (!aggregatedByDevice[device_uid]) {
        aggregatedByDevice[device_uid] = {};
      }

      if (!aggregatedByDevice[device_uid][source_type_id]) {
        aggregatedByDevice[device_uid][source_type_id] = createAggregatedSource(
          device_uid,
          source_type_id,
          name,
          last_seen ?? ''
        );
      }

      const aggregatedSource = aggregatedByDevice[device_uid][source_type_id];

      /* Track highest status code */
      if (status_code != null) {
        aggregatedSource.highest_status_code =
          aggregatedSource.highest_status_code == null
            ? status_code
            : Math.max(aggregatedSource.highest_status_code, status_code);

        aggregatedSource.status = calculateStatusFromStatusCode(
          String(aggregatedSource.highest_status_code)
        );
      }

      /* Group counts */
      const prefix = status_code != null ? String(status_code)[0] : null;

      if (!prefix) {
        aggregatedSource.group_null =
          (aggregatedSource.group_null ?? 0) + record_count;
      } else {
        const key = statusGroupMap[prefix];
        if (key)
          aggregatedSource[key] =
            (typeof aggregatedSource[key] === 'number'
              ? aggregatedSource[key]
              : 0) + record_count;
      }
    }
  );

  // Flatten for UI
  return Object.entries(aggregatedByDevice).flatMap(([device_uid, sources]) =>
    Object.entries(sources).map(([source_type_id, aggregatedSource]) => {
      const highest_status = getHighestStatus(aggregatedSource);

      const statusGroups: StatusGroup[] = [
        {
          label: 'Operational',
          sourceCount: aggregatedSource.group_2xxx ?? 0,
          codePrefix: '2',
          color: 'Operational'
        },
        {
          label: 'Warning',
          sourceCount: aggregatedSource.group_3xxx ?? 0,
          codePrefix: '3',
          color: 'Warning'
        },
        {
          label: 'Error',
          sourceCount: aggregatedSource.group_4xxx ?? 0,
          codePrefix: '4',
          color: 'Error'
        },
        {
          label: 'Disabled',
          sourceCount:
            (aggregatedSource.group_1xxx ?? 0) +
            (aggregatedSource.group_null ?? 0),
          codePrefix: '1/null',
          color: 'Disabled'
        }
      ].filter((g) => g.sourceCount > 0);

      return {
        ...aggregatedSource,
        device_uid,
        source_type_id: Number(source_type_id),
        highest_status,
        highest_status_code: aggregatedSource.highest_status_code,
        statusGroups
      };
    })
  );
}
